/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { MEDIA_QUERY_FOR_PC } from "constants/mediaQueryForPc";
import { useI18n } from "hooks/i18n/i18n";
import { withNoTranslate } from "i18n/noTranslate";
import { preprocessTextForWeglotTranslation } from "i18n/textTransformations";
import React from "react";
import { Link } from "react-router-dom";
import { AspectRatioImage } from "ui/AspectRatioImage";
import { getEventDisplayInfo } from "utility";
import { changeStyleWithHosting } from "utility/hosting";

import { AccountLotteryStatus } from "./AccountLotteryStatus";

import type { LotteryTicketRecord } from "./useGetLotteryTicketList";
import type { Event, Place } from "@spwn/types/firebase/firestore";

type Props = {
  event: Event;
  tickets: LotteryTicketRecord[];
  places?: Place[];
};

const DATE_LOCALES: Record<string, string> = {
  en: "en-US",
  es: "es-ES",
  ja: "ja-JP",
  ko: "ko-KR",
  zh_cn: "zh-CN",
  zh_tw: "zh-TW",
};

export const AccountLotteryItem: React.FC<Props> = ({
  event,
  tickets,
  places,
}) => {
  const { t, i18n } = useI18n();
  const classes = styles();

  const eventId = event._id ?? "";
  const displayInfo = getEventDisplayInfo(event);
  const firstTicketPlace = places?.find(
    (place) => place.code === tickets[0]?.ticket.place
  );
  const eventStartAt = event.parts[0]?.startTime.toDate();
  const eventDateTime = eventStartAt
    ? new Intl.DateTimeFormat(DATE_LOCALES[i18n.language] ?? "ja-JP", {
        day: "2-digit",
        hour: "2-digit",
        hour12: false,
        minute: "2-digit",
        month: "2-digit",
        weekday: "short",
      }).format(eventStartAt)
    : "";

  return (
    <li css={classes.applicationItem}>
      <Link to={`/events/${eventId}/ticket`} css={classes.applicationLink}>
        <div css={classes.thumbnail}>
          <AspectRatioImage
            src={displayInfo.thumbnail}
            alt={displayInfo.eventTitle}
            aspectRatio={16 / 9}
          />
        </div>

        <div css={classes.applicationInfo}>
          {/*
            @description
            現状Notebookの抽選処理を実行すると、tickets/{eventId}/lotteryが全削除されるので、実質エントリー中しか表示出来ない。
          */}
          <AccountLotteryStatus status="inProgress" />

          <p css={classes.eventTitle} {...withNoTranslate()}>
            {displayInfo.eventTitle}
          </p>

          {firstTicketPlace && (
            <p css={classes.placeName}>{firstTicketPlace.name}</p>
          )}

          {eventDateTime && (
            <p css={classes.eventDateTime}>
              {eventDateTime} {t("account.history.performanceStarts")}
            </p>
          )}

          <p css={classes.applicationType}>
            {t("account.history.applicationTypeLottery")}
          </p>

          <div css={classes.ticketList}>
            {tickets.map(({ purchaseCount, ticket }) => (
              <p key={ticket._id} css={classes.ticket}>
                {t("account.history.ticketQuantity", {
                  quantity: purchaseCount,
                  ticketName: preprocessTextForWeglotTranslation(ticket.name),
                })}
              </p>
            ))}
          </div>
        </div>

        <ChevronRightIcon css={classes.chevron} aria-hidden="true" />
      </Link>
    </li>
  );
};

const styles = () => {
  const { accentColor } = changeStyleWithHosting().baseStyles;

  return {
    applicationItem: css({
      minWidth: 0,
      backgroundColor: "#fff",
      border: "1px solid #a8a8a8",
    }),
    applicationLink: css({
      display: "grid",
      gridTemplateColumns: "96px minmax(0, 1fr) 24px",
      alignItems: "center",
      gap: "16px",
      minHeight: "128px",
      padding: "16px",
      color: "#111",
      textDecoration: "none",
      [MEDIA_QUERY_FOR_PC]: {
        gridTemplateColumns: "112px minmax(0, 1fr) 24px",
        minHeight: "160px",
      },
      "&:hover": {
        backgroundColor: "#fafafa",
      },
      "&:focus-visible": {
        outline: `2px solid ${accentColor}`,
        outlineOffset: "2px",
      },
    }),
    thumbnail: css({
      width: "96px",
      backgroundColor: "#eee",
      [MEDIA_QUERY_FOR_PC]: {
        width: "112px",
      },
    }),
    applicationInfo: css({
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      minWidth: 0,
    }),
    eventTitle: css({
      margin: 0,
      fontSize: "14px",
      fontWeight: "bold",
      lineHeight: 1.4,
    }),
    placeName: css({
      margin: 0,
      fontSize: "14px",
      fontWeight: "bold",
      lineHeight: 1.4,
    }),
    eventDateTime: css({
      margin: "4px 0 0",
      color: "#5c5c5c",
      fontSize: "12px",
      lineHeight: 1.4,
    }),
    applicationType: css({
      margin: "4px 0 0",
      fontSize: "14px",
      fontWeight: "bold",
      lineHeight: 1.4,
    }),
    ticketList: css({
      display: "flex",
      flexDirection: "column",
      marginTop: "2px",
    }),
    ticket: css({
      margin: 0,
      fontSize: "14px",
      lineHeight: 1.4,
    }),
    chevron: css({
      color: "#999",
      fontSize: "20px",
    }),
  };
};
